import React, { useState, useEffect, useRef } from "react";
import { Row, Col, Card, CardBody, Button, Form } from "reactstrap";
import CommonTableIndexWise from "Components/Common/CommonTableIndexWise";
import CommonForm, {
  FormFieldConfig,
} from "Components/Common/CommonForm";
import sf from "assets/scss/common/searchFilter.module.scss";
import ac from "../css/accessioning.module.scss";
import { useForm } from "react-hook-form";
import { useScanSampleSeperationService } from "modules/Session/Session/api/useSession";
// import { useScanSampleSeperationService } from "hooks/Library/AccessioningSession/useSession";
import { useQueryClient } from "@tanstack/react-query";
import { formatDate } from "common/service/CommonService";
import { formatTime24Hour } from "../helper/sessionlogconstant";
import { getScanFields } from "../helper/accessioningconstant";

interface VacutainerToAliquotsProps {
  sessionData?: any;
  sessionid?: string;
  isParentModalOpen?: boolean;
}


const VacutainerToAliquots: React.FC<VacutainerToAliquotsProps> = ({
  sessionData,
  sessionid,
  isParentModalOpen,
}) => {
  const [vacutainerStatus, setVacutainerStatus] = useState<
    "idle" | "matched" | "mismatch"
  >("idle");
  const [aliquotStatus, setAliquotStatus] = useState<
    "idle" | "matched" | "mismatch"
  >("idle");
  const [formMessage, setFormMessage] = useState<{
    text: string;
    type: "success" | "error" | "";
  }>({ text: "", type: "" });
  const [isSpeaking, setIsSpeaking] = useState(false);

  const totalSubjectsCount = sessionData?.total_subjects ?? sessionData?.totalsubjects ?? 0;
  const skippedSubjectsCount = sessionData?.skipped_subjects ?? 0;
  const aliquotsPerSub = sessionData?.total_aliquots || 2;
  const totalExpectedAliquots = totalSubjectsCount * aliquotsPerSub;
  const totalEffectiveCompletedAliquots = (sessionData?.completed_aliquots ?? 0) + (skippedSubjectsCount * aliquotsPerSub);

  const isSeperationScanCompleted =
    sessionData?.display_status === "COMPLETED" ||
    sessionData?.sessionstate === "completed" ||
    (totalSubjectsCount > 0 &&
      skippedSubjectsCount === 0 &&
      totalEffectiveCompletedAliquots >= totalExpectedAliquots);

  const [isShowingAllScans, setIsShowingAllScans] = useState(false);
  const [searchText, setSearchText] = useState<string>("");

  const [searchFocused, setSearchFocused] = useState(false);
  const [currentPageNumber, setcurrentPageNumber] = useState(1);
  const [activeAliquotIndex, setActiveAliquotIndex] = useState(0);
  const totalAliquots = sessionData?.total_aliquots || 0;

  const queryClient = useQueryClient();

  const { InsertMutation: ScanInsertMutation, ValidateVaccutainerMutation, SelectAllQuery } =
    useScanSampleSeperationService({
      sessionid: sessionid ? parseInt(sessionid) : sessionData?.sessionid || 1,
      searchtext: searchText || "",
      currentpage: currentPageNumber,
      pagesize: 10,
    });

  // Prepare default values for dynamic aliquots
  const dynamicDefaultValues: any = { vacutainerBarcode: "" };
  for (let i = 1; i <= totalAliquots; i++) {
    dynamicDefaultValues[`aliquotBarcode_${i}`] = "";
  }

  const {
    control,
    handleSubmit,
    reset,
    watch,
    setError,
    clearErrors,
    setValue,
  } = useForm({
    defaultValues: dynamicDefaultValues,
  });

  const vacutainerBarcode = watch("vacutainerBarcode");
  const allValues = watch();

  // Scanner vs Manual Detection
  const lastKeyTimeRef = useRef<number>(0);
  const keystrokeIntervalsRef = useRef<number[]>([]);

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (
        e.key === "Enter" ||
        e.key === "Shift" ||
        e.key === "Control" ||
        e.key === "Tab"
      )
        return;
      const now = Date.now();
      if (lastKeyTimeRef.current !== 0) {
        const gap = now - lastKeyTimeRef.current;
        if (gap < 1000) keystrokeIntervalsRef.current.push(gap);
      }
      lastKeyTimeRef.current = now;
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, []);

  useEffect(() => {
    let attempts = 0;
    const focusInterval = setInterval(() => {
      try {
        const targetName =
          vacutainerStatus === "idle"
            ? "vacutainerBarcode"
            : `aliquotBarcode_${activeAliquotIndex}`;
        const input = document.getElementsByName(
          targetName,
        )[0] as HTMLInputElement;
        if (input && !input.disabled) {
          input.focus();
          if (document.activeElement === input || attempts >= 15) {
            clearInterval(focusInterval);
          }
        }
        attempts++;
        if (attempts >= 15) clearInterval(focusInterval);
      } catch (err) {
        attempts++;
      }
    }, 300);

    return () => clearInterval(focusInterval);
  }, [
    vacutainerStatus,
    activeAliquotIndex,
    isShowingAllScans,
    isParentModalOpen,
  ]);

  const activeUtteranceRef = useRef<SpeechSynthesisUtterance | null>(null);
  const speechTimeoutRef = useRef<any>(null);
  const isMountedRef = useRef(true);

  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
      if (speechTimeoutRef.current) {
        clearTimeout(speechTimeoutRef.current);
        speechTimeoutRef.current = null;
      }
      if (activeUtteranceRef.current && "speechSynthesis" in window) {
        window.speechSynthesis.cancel();
        activeUtteranceRef.current = null;
      }
    };
  }, []);

  const speak = (msg: string, onEndCallback?: () => void) => {
    if (!("speechSynthesis" in window)) {
      onEndCallback?.();
      return;
    }

    const synth = window.speechSynthesis;

    if (speechTimeoutRef.current) {
      clearTimeout(speechTimeoutRef.current);
      speechTimeoutRef.current = null;
    }

    if (synth.speaking || synth.pending) {
      synth.cancel();
    }

    if (isMountedRef.current) {
      setIsSpeaking(true);
    }

    // 150ms delay to allow browser speech synthesis engine to clear/reset queue
    speechTimeoutRef.current = setTimeout(() => {
      if (!isMountedRef.current) return;
      try {
        const utterance = new SpeechSynthesisUtterance(msg);
        utterance.rate = 1.0;
        utterance.lang = "en-US";

        const voices = synth.getVoices();
        const voice =
          voices.find((v) => v.lang === "en-US") ||
          voices.find((v) => v.lang.startsWith("en")) ||
          voices[0];

        if (voice) utterance.voice = voice;
        activeUtteranceRef.current = utterance;

        const cleanUp = () => {
          if (isMountedRef.current) {
            setIsSpeaking(false);
          }
          activeUtteranceRef.current = null;
        };

        utterance.onend = () => {
          cleanUp();
          if (isMountedRef.current) {
            onEndCallback?.();
          }
        };

        utterance.onerror = () => {
          cleanUp();
          if (isMountedRef.current) {
            onEndCallback?.();
          }
        };

        synth.speak(utterance);
      } catch (err) {
        console.error("SpeechSynthesis error in VacutainerToAliquots:", err);
        if (isMountedRef.current) {
          setIsSpeaking(false);
          onEndCallback?.();
        }
      }
    }, 150);
  };

  const clearAndRestart = () => {
    setVacutainerStatus("idle");
    setAliquotStatus("idle");
    setFormMessage({ text: "", type: "" });
    setActiveAliquotIndex(0);
    clearErrors();
    reset(dynamicDefaultValues);
    setTimeout(() => {
      const firstInput = document.getElementsByName(
        "vacutainerBarcode",
      )[0] as HTMLInputElement;
      if (firstInput) firstInput.focus();
    }, 100);
  };

  const verifyVacutainerAliquot = (
    vac_barcode: string,
    ali_barcode: string,
    expected_tag: number,
  ) => {
    if (vac_barcode.length !== 10) return "Invalid vacutainer barcode";
    if (ali_barcode.length !== 10) return "Invalid aliquot barcode";

    // Extract values
    const vac_type = parseInt(vac_barcode.substring(9, 10));
    const ali_type = parseInt(ali_barcode.substring(9, 10));
    const period = parseInt(ali_barcode.substring(6, 7));
    const timepointBarcode = ali_barcode.substring(7, 9);

    const vac_prefix = vac_barcode.substring(0, 9);
    const ali_prefix = ali_barcode.substring(0, 9);

    const timepointSession =
      sessionData?.timepoint_sort < 10
        ? `0${sessionData?.timepoint_sort}`
        : `${sessionData?.timepoint_sort}`;

    // VALIDATIONS
    if (vac_type !== 1) return "Invalid vacutainer type";
    if (ali_type < 2) return "Not a valid aliquot barcode";

    if (period !== sessionData?.period_sort) return "Invalid period";
    if (timepointBarcode !== timepointSession) return "Invalid timepoint";

    if (ali_type !== expected_tag) return "Aliquot sequence mismatch";
    if (vac_prefix !== ali_prefix) return "Barcode Mismatch";

    return "success";
  };

  const handleScanSubmit = (data: any, e?: React.BaseSyntheticEvent) => {
    clearErrors();
    const hasVacutainer =
      data.vacutainerBarcode && data.vacutainerBarcode.trim() !== "";
    const submitterName = (e?.nativeEvent as any)?.submitter?.name;
    const isManualClick = submitterName === "verifyButton";

    keystrokeIntervalsRef.current = [];
    lastKeyTimeRef.current = 0;

    if (hasVacutainer) {
      if (vacutainerStatus === "idle") {
        const vacBarcode = data.vacutainerBarcode.trim();
        if (vacBarcode.length !== 10) {
          setError("vacutainerBarcode", {
            type: "manual",
            message: "Invalid vacutainer barcode length",
          });
          setFormMessage({
            text: "Invalid vacutainer barcode length.",
            type: "error",
          });
          speak("Error. Invalid vacutainer barcode length.", () => {
            setValue("vacutainerBarcode", "");
            clearErrors("vacutainerBarcode");
            setFormMessage({ text: "", type: "" });
            setTimeout(() => {
              const input = document.getElementsByName(
                "vacutainerBarcode",
              )[0] as HTMLInputElement;
              if (input) input.focus();
            }, 100);
          });
          return;
        }

        const vac_type = parseInt(vacBarcode.substring(9, 10));
        const vac_period = parseInt(vacBarcode.substring(6, 7));
        const vac_timepoint = vacBarcode.substring(7, 9);
        const timepointSession =
          sessionData?.timepoint_sort < 10
            ? `0${sessionData?.timepoint_sort}`
            : `${sessionData?.timepoint_sort}`;

        if (vac_period !== sessionData?.period_sort) {
          setError("vacutainerBarcode", {
            type: "manual",
            message: "Invalid period",
          });
          setFormMessage({ text: "Invalid period", type: "error" });
          speak("Error. Invalid period.", () => {
            setValue("vacutainerBarcode", "");
            clearErrors("vacutainerBarcode");
            setFormMessage({ text: "", type: "" });
            setTimeout(() => {
              const input = document.getElementsByName(
                "vacutainerBarcode",
              )[0] as HTMLInputElement;
              if (input) input.focus();
            }, 100);
          });
          return;
        }

        if (vac_timepoint !== timepointSession) {
          setError("vacutainerBarcode", {
            type: "manual",
            message: "Invalid timepoint",
          });
          setFormMessage({ text: "Invalid timepoint", type: "error" });
          speak("Error. Invalid timepoint.", () => {
            setValue("vacutainerBarcode", "");
            clearErrors("vacutainerBarcode");
            setFormMessage({ text: "", type: "" });
            setTimeout(() => {
              const input = document.getElementsByName(
                "vacutainerBarcode",
              )[0] as HTMLInputElement;
              if (input) input.focus();
            }, 100);
          });
          return;
        }

        if (vac_type !== 1) {
          setError("vacutainerBarcode", {
            type: "manual",
            message: "Invalid vacutainer type",
          });
          setFormMessage({ text: "Invalid vacutainer type.", type: "error" });
          speak("Error. Invalid vacutainer type.", () => {
            setValue("vacutainerBarcode", "");
            clearErrors("vacutainerBarcode");
            setFormMessage({ text: "", type: "" });
            setTimeout(() => {
              const input = document.getElementsByName(
                "vacutainerBarcode",
              )[0] as HTMLInputElement;
              if (input) input.focus();
            }, 100);
          });
          return;
        }

        ValidateVaccutainerMutation.mutate(
          {
            sessionid: sessionid
              ? parseInt(sessionid)
              : sessionData?.sessionid ,
            vaccutainerbarcode: vacBarcode,
          },
          {
            onSuccess: (res: any) => {
              setVacutainerStatus("matched");
              setActiveAliquotIndex(1);
              const focusFirstAliquot = () => {
                setTimeout(() => {
                  const nextInput = document.getElementsByName(
                    `aliquotBarcode_1`,
                  )[0] as HTMLInputElement;
                  if (nextInput) nextInput.focus();
                }, 100);
              };

              const msg =
                res?.message || "Vacutainer Scanned. Waiting for aliquots.";
              setFormMessage({
                text: msg,
                type: "success",
              });
              speak(msg, focusFirstAliquot);
            },
            onError: (err: any) => {
              const errorData = err?.response?.data || err;
              let backendMsg =
                typeof errorData === "string"
                  ? errorData
                  : errorData?.message ||
                    errorData?.resultmessage ||
                    errorData?.result ||
                    err?.message;

              const parsedMsg = Array.isArray(backendMsg)
                ? backendMsg.join(", ")
                : backendMsg;
              const displayMsg =
                typeof parsedMsg === "string" && parsedMsg
                  ? parsedMsg
                  : "Invalid vacutainer barcode";

              setVacutainerStatus("mismatch");
              setFormMessage({ text: displayMsg, type: "error" });
              speak(`Error. ${displayMsg}`, () => {
                setVacutainerStatus("idle");
                setValue("vacutainerBarcode", "");
                clearErrors("vacutainerBarcode");
                setFormMessage({ text: "", type: "" });
                setTimeout(() => {
                  const input = document.getElementsByName(
                    "vacutainerBarcode",
                  )[0] as HTMLInputElement;
                  if (input) input.focus();
                }, 100);
              });
            },
          }
        );
        return;
      }

      // Check the current active aliquot
      const currentAliquotVal = data[`aliquotBarcode_${activeAliquotIndex}`];
      if (currentAliquotVal && currentAliquotVal.trim() !== "") {
        const validationResult = verifyVacutainerAliquot(
          data.vacutainerBarcode.trim(),
          currentAliquotVal.trim(),
          activeAliquotIndex + 1,
        );

        if (validationResult === "success") {
          const isLast = activeAliquotIndex === totalAliquots;

          ScanInsertMutation.mutate(
            {
              sessionid: sessionid
                ? parseInt(sessionid)
                : sessionData?.sessionid || 1,
              aliquotbarcode: currentAliquotVal.trim(),
              vaccutainerbarcode: data.vacutainerBarcode.trim(),
              result: "valid",
              resultmessage: "Matched Successfully",
            },
            {
              onSuccess: (res: any) => {
                queryClient.invalidateQueries({
                  queryKey: ["scan-sample-seperation-select-all"],
                });
                queryClient.invalidateQueries({ queryKey: ["session-select"] });
                queryClient.invalidateQueries({ queryKey: ["session-progress"] });
                const msg =
                  res?.message ||
                  (isLast
                    ? "Match Verified."
                    : `Aliquot ${activeAliquotIndex} matched.`);

                if (isLast) {
                  setFormMessage({ text: msg, type: "success" });
                  speak("Barcode Matched.", () => clearAndRestart());
                } else {
                  setFormMessage({ text: msg, type: "success" });
                  const nextIndex = activeAliquotIndex + 1;
                  setActiveAliquotIndex(nextIndex);
                  speak("Barcode Matched.", () => {
                    setFormMessage({ text: "", type: "" });
                    setTimeout(() => {
                      const nextInput = document.getElementsByName(
                        `aliquotBarcode_${nextIndex}`,
                      )[0] as HTMLInputElement;
                      if (nextInput) nextInput.focus();
                    }, 100);
                  });
                }
              },
              onError: (err: any) => {
                // 1. Get the actual error payload whether it's wrapped in Axios response or not
                const errorData = err?.response?.data || err;

                // 2. Extract the message directly from the backend's JSON structure or string
                let backendMsg =
                  typeof errorData === "string"
                    ? errorData
                    : errorData?.message ||
                      errorData?.resultmessage ||
                      errorData?.result ||
                      err?.message;

                const parsedMsg = Array.isArray(backendMsg)
                  ? backendMsg.join(", ")
                  : backendMsg;
                const displayMsg =
                  typeof parsedMsg === "string" && parsedMsg
                    ? parsedMsg
                    : "Failed to record scan";

                // 3. Check if it's an "already exists" error (by status code or text)
                const is409 =
                  err?.response?.status === 409 ||
                  errorData?.statusCode === 409 ||
                  errorData?.status === 409 ||
                  displayMsg.toLowerCase().includes("already exist");

                // 4. Set final messages
                const speakMsg = is409 ? "Already exists" : displayMsg;

                setFormMessage({ text: displayMsg, type: "error" });
                speak(`Error. ${speakMsg}`, () => clearAndRestart());
                // speak(`Error. ${speakMsg}`, () => {
                //   setValue(`aliquotBarcode_${activeAliquotIndex}`, "");
                //   clearErrors(`aliquotBarcode_${activeAliquotIndex}`);
                //   setValue("vacutainerBarcode", "");
                //   setFormMessage({ text: "", type: "" });
                //   setTimeout(() => {
                //     const input = document.getElementsByName(`aliquotBarcode_${activeAliquotIndex}`)[0] as HTMLInputElement;
                //     if (input) input.focus();
                //   }, 100);
                // });
              },
            },
          );
          return;
        } else {
          // Failure API Call
          if (validationResult === "Barcode Mismatch") {
            ScanInsertMutation.mutate(
              {
                sessionid: sessionid
                  ? parseInt(sessionid)
                  : sessionData?.sessionid || 1,
                aliquotbarcode: currentAliquotVal.trim(),
                vaccutainerbarcode: data.vacutainerBarcode.trim(),
                result: "invalid",
                resultmessage: "mismatched barcode",
              },
              {
                onSuccess: () => {
                  queryClient.invalidateQueries({
                    queryKey: ["scan-sample-seperation-select-all"],
                  });
                  queryClient.invalidateQueries({
                    queryKey: ["session-select"],
                  });
                  queryClient.invalidateQueries({
                    queryKey: ["session-progress"],
                  });
                },
              },
            );
          }

          setError(`aliquotBarcode_${activeAliquotIndex}`, {
            type: "manual",
            message: validationResult,
          });
          setFormMessage({ text: validationResult, type: "error" });
          speak(`Error. ${validationResult}`, () => {
            setValue(`aliquotBarcode_${activeAliquotIndex}`, "");
            clearErrors(`aliquotBarcode_${activeAliquotIndex}`);
            setFormMessage({ text: "", type: "" });
            setTimeout(() => {
              const input = document.getElementsByName(
                `aliquotBarcode_${activeAliquotIndex}`,
              )[0] as HTMLInputElement;
              if (input) input.focus();
            }, 100);
          });
          return;
        }
      } else if (isManualClick) {
        // Aliquot field is empty and Verify button was clicked
        setError(`aliquotBarcode_${activeAliquotIndex}`, {
          type: "manual",
          message: "Aliquot barcode cannot be empty.",
        });
        setFormMessage({
          text: "Aliquot barcode cannot be empty.",
          type: "error",
        });
        speak("Error. Aliquot barcode cannot be empty.");
        return;
      }
    }

    if (!hasVacutainer) {
      setError("vacutainerBarcode", {
        type: "manual",
        message: "Vacutainer barcode cannot be empty.",
      });
      setFormMessage({
        text: "Vacutainer barcode cannot be empty.",
        type: "error",
      });
      speak("Error. Vacutainer barcode cannot be empty.", () =>
        clearAndRestart(),
      );
      return;
    }
  };

  const columns = [
    {
      key: "scandate",
      label: "Date",
      render: (_: any, row?: any) =>
        row?.scandate ? (
          <div className={`${ac.cellText}`}>
            {formatDate(row.scandate)}
          </div>
        ) : null,
    },
    {
      key: "scantime",
      label: "Time",
      render: (_: any, row?: any) =>
        row?.scandate ? (
          <div className={`${ac.cellText}`}>
            {formatTime24Hour(row.scandate)}
          </div>
        ) : null,
    },
    {
      key: "vaccutainerbarcode",
      label: "Vacutainer",
      render: (_: any, row?: any) =>
        row ? (
          <div
            className={`fw-600 text-truncate ${ac.cellText}`}
            style={{ maxWidth: "90px" }}
            title={row.vaccutainerbarcode}
          >
            {row.vaccutainerbarcode}
          </div>
        ) : null,
    },
    {
      key: "aliquotbarcode",
      label: "Aliquot",
      render: (_: any, row?: any) =>
        row ? (
          <div
            className={`text-truncate ${ac.cellText}`}
            style={{ maxWidth: "90px" }}
            title={row.aliquotbarcode}
          >
            {row.aliquotbarcode}
          </div>
        ) : null,
    },
    {
      key: "result",
      label: "Status",
      render: (_: any, row?: any) =>
        row ? (
          <span
            className={`badge ${row.result === "valid" ? "bg-success" : "bg-danger"} bg-opacity-10 ${row.result === "valid" ? "text-success" : "text-danger"} border ${row.result === "valid" ? "border-success" : "border-danger"} border-opacity-25 px-2 py-1 text-uppercase`}
            style={{ minWidth: "80px", display: "inline-block" }}
          >
            <i
              className={`${row.result === "valid" ? "ri-checkbox-circle-line" : "ri-error-warning-line"} me-1`}
            ></i>{" "}
            {row.result}
          </span>
        ) : null,
    },
    {
      key: "resultmessage",
      label: "Result",
      render: (_: any, row?: any) =>
        row ? (
          <div
            className={`text-muted text-truncate ${ac.cellText}`}
            style={{ maxWidth: "100px" }}
            title={row.resultmessage || "-"}
          >
            {row.resultmessage || "-"}
          </div>
        ) : null,
    },
  ];

  const columnsAll = [
    {
      key: "scandate",
      label: "Date",
      render: (_: any, row?: any) =>
        row?.scandate ? (
          <div className={`${ac.cellText}`}>
            {formatDate(row.scandate)}
          </div>
        ) : null,
    },
    {
      key: "scantime",
      label: "Time",
      render: (_: any, row?: any) =>
        row?.scandate ? (
          <div className={`${ac.cellText}`}>
            {formatTime24Hour(row.scandate)}
          </div>
        ) : null,
    },
    {
      key: "vaccutainerbarcode",
      label: "Vacutainer Barcode",
      render: (_: any, row?: any) =>
        row ? (
          <div className={`${ac.cellText}`}>{row.vaccutainerbarcode}</div>
        ) : null,
    },
    {
      key: "aliquotbarcode",
      label: "Aliquot Barcode",
      render: (_: any, row?: any) =>
        row ? (
          <div className={`${ac.cellText}`}>{row.aliquotbarcode}</div>
        ) : null,
    },
    {
      key: "result",
      label: "Status",
      render: (_: any, row?: any) =>
        row ? (
          <span
            className={`badge ${row.result === "valid" ? "bg-success" : "bg-danger"} bg-opacity-10 ${row.result === "valid" ? "text-success" : "text-danger"} border ${row.result === "valid" ? "border-success" : "border-danger"} border-opacity-25 px-2 py-1 rounded-pill text-uppercase`}
            style={{ minWidth: "80px", display: "inline-block" }}
          >
            <i
              className={`${row.result === "valid" ? "ri-checkbox-circle-line" : "ri-error-warning-line"} me-1`}
            ></i>{" "}
            {row.result}
          </span>
        ) : null,
    },
    {
      key: "resultmessage",
      label: "Result",
      render: (_: any, row?: any) =>
        row ? (
          <div className={`${ac.cellText}`}>{row.resultmessage || "-"}</div>
        ) : null,
    },
  ];

  const scanFields = getScanFields("vacutainerToAliquot", {
    isSessionCompleted: isSeperationScanCompleted,
    step1Status: vacutainerStatus,
    step2Status: aliquotStatus,
    isSpeaking,
    isPending: ScanInsertMutation.isPending,
    totalAliquots,
    activeAliquotIndex,
  });

  return (
    <>
      {isShowingAllScans ? (
        (() => {
          const historyData = SelectAllQuery?.data?.data || [];
          const totalRecords = historyData?.[0]?.total_count || 0;
          return (
            <div className={ac.allScansContainer}>
              <div className="d-flex justify-content-between align-items-center mb-4">
                <div className="d-flex align-items-center gap-3">
                  <Button
                    color="light"
                    outline
                    className="px-2 py-1 bg-white border rounded"
                    onClick={() => setIsShowingAllScans(false)}
                  >
                    <i className="ri-arrow-left-line text-dark"></i>
                  </Button>
                  <h5
                    className={`fw-bold text-dark mb-0 d-flex align-items-center gap-2 ${ac.allScansTitle}`}
                  >
                    <i className="ri-history-line text-muted"></i> All
                    Vacutainer to Aliquot Scans
                    <span
                      className={`badge bg-light text-dark ms-1 border ${ac.allScansBadge}`}
                    >
                      {totalRecords}
                    </span>
                  </h5>
                </div>
                <div className="d-flex gap-2 align-items-center">
                  <div
                    className={`${sf.searchBox} ${searchFocused ? sf.focused : ""}`}
                  >
                    <i className={`ri-search-line ${sf.searchIcon}`} />
                    <input
                      type="text"
                      placeholder="Search barcodes..."
                      value={searchText ?? ""}
                      className={sf.searchInput}
                      onChange={(e) => {
                        setSearchText(e.target.value);
                        setcurrentPageNumber(1);
                      }}
                      onFocus={() => setSearchFocused(true)}
                      onBlur={() => setSearchFocused(false)}
                    />
                    {searchText && (
                      <i
                        className={`ri-close-line ${sf.clearIcon}`}
                        onClick={() => {
                          setSearchText("");
                          setcurrentPageNumber(1);
                        }}
                      />
                    )}
                  </div>
                </div>
              </div>
              <div>
                <CommonTableIndexWise
                  columns={columnsAll}
                  data={historyData}
                  loading={SelectAllQuery.isFetching}
                  pageSize={10}
                  currentPage={currentPageNumber}
                  totalRecords={totalRecords}
                  onPageChange={setcurrentPageNumber}
                  actions={{}}
                />
              </div>
            </div>
          );
        })()
      ) : (
        <div className={ac.scanContentWrapper}>
          <Row className="gx-1">
            {/* Left Panel */}
            <Col md={5}>
              <Card className="border shadow-none h-100 rounded-0">
                <CardBody className="p-4 pb-0">
                  <div className={ac.sectionHeader}>
                    <h6 className="fw-bold text-dark mb-1 d-flex align-items-center gap-2">
                      <i className="ri-barcode-line text-muted"></i> Barcode
                      Scanning
                    </h6>
                    <p className="text-muted mb-0">
                      Follow the sequence below to accession aliquots.
                    </p>
                  </div>

                  <Form onSubmit={handleSubmit(handleScanSubmit)}>
                    <button
                      type="submit"
                      name="hiddenSubmit"
                      style={{ display: "none" }}
                    />

                    <CommonForm
                      fields={scanFields}
                      control={control}
                      columns={1}
                    />

                    <div className={ac.formActions}>
                      <Button
                        color="light"
                        outline
                        onClick={clearAndRestart}
                        type="button"
                        disabled={isSpeaking || isSeperationScanCompleted}
                      >
                        Clear
                      </Button>
                      <Button
                        color="primary"
                        outline
                        type="submit"
                        name="verifyButton"
                        disabled={
                          !vacutainerBarcode ||
                          ScanInsertMutation.isPending ||
                          isSpeaking ||
                          isSeperationScanCompleted
                        }
                      >
                        Verify Match
                      </Button>
                    </div>

                    {formMessage.text && (
                      <div
                        className={
                          formMessage.type === "success"
                            ? ac.feedbackSuccess
                            : ac.feedbackError
                        }
                      >
                        <i
                          className={`${formMessage.type === "success" ? "ri-checkbox-circle-fill text-success" : "ri-error-warning-fill text-danger"} fs-18 mt-1`}
                        ></i>
                        <div>
                          <p
                            className={
                              formMessage.type === "success"
                                ? "text-success"
                                : "text-danger"
                            }
                          >
                            <span className="fw-bold">
                              {formMessage.type === "success"
                                ? "Success:"
                                : "Error:"}
                            </span>{" "}
                            {formMessage.text}
                          </p>
                        </div>
                      </div>
                    )}
                  </Form>
                </CardBody>
              </Card>
            </Col>

            {/* Right Panel */}
            <Col md={7}>
              <Card className="border shadow-none mb-0 h-100 rounded-0">
                <CardBody className="p-0 d-flex flex-column h-100">
                  <div className="d-flex justify-content-between align-items-center p-3 border-bottom">
                    <h6 className="fw-bold text-dark mb-0 d-flex align-items-center gap-2">
                      <i className="ri-history-line text-muted"></i> Recent
                      Vacutainer to Aliquot Scans
                    </h6>
                  </div>

                  <div className={`flex-grow-1 p-2 ${ac.scansScrollArea}`}>
                    <CommonTableIndexWise
                      columns={columns}
                      data={SelectAllQuery?.data?.data.slice(0, 5) || []}
                      loading={SelectAllQuery.isFetching}
                      maxHeight="200px"
                    />
                  </div>

                  <div
                    className={`p-3 border-top text-center ${ac.viewAllBar}`}
                    onClick={() => setIsShowingAllScans(true)}
                  >
                    <span className="fw-bold text-dark">
                      View All Session Scans
                    </span>
                  </div>
                </CardBody>
              </Card>
            </Col>
          </Row>
        </div>
      )}
    </>
  );
};

export default VacutainerToAliquots;
